import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleDollarSign,
  Copy,
  Loader2,
  Mail,
  MailOpen,
  MailX,
  PauseCircle,
  PhoneCall,
  PhoneMissed,
  RefreshCw,
  ScrollText,
  UserCheck,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import PrivateLayout from '../../layouts/PrivateLayout';
import { money, fmtDate, fmtDateTime } from '../shared/billingFormat';
import { InvoiceStatusPill } from '../shared/badges';
import { useVaRunReport } from '../../../services/vaRuns';

// Human labels for the batch's manual-review reasons. Background: the VA
// hotline only reads back a DOLLAR AMOUNT; the rating is reverse-engineered
// from it via the VA pay tables. These reasons are the ways that lookup can
// fail, and each hover explains it in plain English for the CM.
const REASON_LABELS = {
  no_exact_matrix_match: 'Amount matched no rating profile',
  ambiguous_matrix_match: 'Several rating profiles matched',
  non_compensation_payment_type: 'Payment type not comp and pension',
  matrix_validation_failed: 'Amount could not be validated',
  repeated_call_failures: 'Repeated call failures',
  retell_manual_rating_discrepancy: 'VA rating differs from manual entry',
  legacy_billing: 'Legacy billing, invoice manually',
};

const REASON_HELP = {
  no_exact_matrix_match:
    "The VA line only tells us a dollar amount; we work out the rating from the VA pay tables. This payment equals NO standard monthly rate (usually a prorated month, back pay rolled in, or money withheld from the check), so the rating can't be computed. No math needed from you: find the client's actual rating (ask them, or their VA letter / va.gov) and enter it with Update Rating.",
  ambiguous_matrix_match:
    'This dollar amount matches MORE than one rating + dependents combination, and what we know about the client did not break the tie. Confirm the actual rating with the client (or their VA letter / va.gov) and enter it with Update Rating.',
  non_compensation_payment_type:
    'The VA said this payment is not disability compensation (for example pension or education benefits), so no disability rating can be inferred from it. Confirm the client\'s actual rating and enter it with Update Rating.',
  matrix_validation_failed:
    'The collected amount could not be validated against the VA pay tables. Confirm the actual rating with the client and enter it with Update Rating.',
  repeated_call_failures:
    'The automated call kept failing for this client, so there is no payment data at all. Often the SSN or date of birth on file does not match VA records. Verify those, or confirm the rating directly with the client.',
  retell_manual_rating_discrepancy:
    'The rating computed from the VA payment does not match the rating a person entered manually. Double-check which is right before anything is billed.',
  legacy_billing:
    'This client is on a pre-2025 legacy agreement, so invoicing is manual by design.',
};

const HANDLED_BY_LABELS = {
  update_rating: 'rating entered',
  manual_invoice: 'invoice created manually',
  failed_queue_apply: 'applied from Failed Calls Queue',
  reconciliation_complete: 'reconciled',
  status_change: 'resolved',
};

// Every client name opens the claim profile's Billing tab in a NEW tab, so
// Madison never loses her place on the board while working items.
const ClaimLink = ({ claimId, className, children }) => (
  <Link
    to={`/admin/profile/${claimId}?tab=billing`}
    target="_blank"
    rel="noopener noreferrer"
    className={className}
  >
    {children}
  </Link>
);

const RatingChange = ({ from, to }) => (
  <span className="inline-flex items-center gap-1 whitespace-nowrap text-sm">
    <span className="text-muted-foreground">{from != null ? `${from}%` : 'new'}</span>
    <ArrowRight className="h-3 w-3 text-muted-foreground" />
    <span className="font-semibold text-foreground">{to}%</span>
  </span>
);

// Green "done" vs amber "still open" state for review items. The board's
// core promise: this flips on its own as CMs and Finance act.
const HandledChip = ({ item }) => {
  if (item.handled) {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-status-success-bg px-2 py-0.5 text-xs font-medium text-status-success-fg"
        title={`Handled ${fmtDateTime(item.handledAt)}${HANDLED_BY_LABELS[item.handledBy] ? ` (${HANDLED_BY_LABELS[item.handledBy]})` : ''}`}
      >
        <Check className="h-3 w-3" />
        Handled {fmtDate(item.handledAt)}
      </span>
    );
  }
  const stale = item.daysOpen >= 7;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        stale
          ? 'bg-status-warning-bg text-status-warning-fg'
          : 'bg-muted text-muted-foreground',
      )}
      title="Clears automatically once the rating is confirmed with Update Rating on the claim profile (or a manual invoice is created for legacy items)."
    >
      Open{item.daysOpen > 0 ? ` ${item.daysOpen}d` : ''}
    </span>
  );
};

// "Did the notification email go out, and was it opened?" from the outbound
// email log. `to` names the recipient in plain terms so "Sent" is never
// ambiguous (legacy items email the billing inbox, reviews email the CM).
const EmailIndicator = ({ email, to = 'CM' }) => {
  // No log row = nothing to show (emails sent before Jul 18, 2026 predate the
  // outbound email log entirely) — render nothing rather than a dangling dash.
  if (!email) return null;
  if (email.status === 'bounced') {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap text-status-error-fg"
        title={`The email to the ${to} BOUNCED - they never got it. Check their email address, and give them a heads up directly.`}
      >
        <MailX className="h-3.5 w-3.5" />
        <span className="text-xs">Bounced</span>
      </span>
    );
  }
  if (email.openedAt) {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap text-status-info-fg"
        title={`The ${to} opened the notification email ${fmtDateTime(email.openedAt)}.`}
      >
        <MailOpen className="h-3.5 w-3.5" />
        <span className="text-xs">{to} opened</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap text-muted-foreground"
      title={`Notification email sent to the ${to} ${fmtDateTime(email.sentAt)}. Not opened yet.`}
    >
      <Mail className="h-3.5 w-3.5" />
      <span className="text-xs">{to} emailed</span>
    </span>
  );
};

// Tint families for section identity chips + count pills.
const ACCENTS = {
  violet: { chip: 'bg-violet-100 text-violet-700' },
  blue: { chip: 'bg-blue-100 text-blue-700' },
  amber: { chip: 'bg-amber-100 text-amber-700' },
  red: { chip: 'bg-red-100 text-red-600' },
  emerald: { chip: 'bg-emerald-100 text-emerald-700' },
  slate: { chip: 'bg-slate-100 text-slate-600' },
};

const CountPill = ({ tone, children }) => (
  <span
    className={cn(
      'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold',
      tone,
    )}
  >
    {children}
  </span>
);

// Icon rides the top-right corner so the value line always gets the tile's
// full width (dollar totals must never truncate). Tiles with an onClick
// scroll to their section and get the hover affordance; the rest are plain
// stats and stay visually inert.
const HeroTile = ({ icon: Icon, accent, label, value, sub, onClick }) => {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
            ACCENTS[accent].chip,
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-1 whitespace-nowrap text-xl font-bold text-foreground">{value}</div>
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </>
  );
  if (!onClick) {
    return <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-sm">{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border border-border bg-card px-4 py-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      {body}
    </button>
  );
};

// Section shell: tinted icon chip for identity, count pills that pull the eye
// to open work, and the explainer as a quiet subtitle. The page should answer
// questions, not raise them.
const SectionCard = ({ icon: Icon, accent = 'slate', title, badges, explainer, children, innerRef }) => (
  <div ref={innerRef} className="scroll-mt-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
    <div className="border-b border-border px-5 py-3.5">
      {/* One aligned row: chip, title, pills. The explainer sits below at
          full width, flush with the px-5 gutter the data rows use. */}
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            ACCENTS[accent].chip,
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <span className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {badges}
        </span>
      </div>
      {explainer ? (
        <p className="mt-2.5 text-xs leading-5 text-muted-foreground">{explainer}</p>
      ) : null}
    </div>
    {children}
  </div>
);

const EmptyRow = ({ children }) => (
  <div className="px-5 py-6 text-center text-sm text-muted-foreground">{children}</div>
);

// "Show all N" row for sections whose long lists stay collapsed by default.
const ShowAllRow = ({ open, onToggle, label }) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex w-full items-center justify-between px-5 py-2.5 text-left text-xs font-medium text-muted-foreground hover:bg-muted/40"
  >
    {open ? 'Hide' : label}
    {open ? (
      <ChevronUp className="h-4 w-4 shrink-0" />
    ) : (
      <ChevronDown className="h-4 w-4 shrink-0" />
    )}
  </button>
);

// Seconds-ago caption that keeps itself fresh between polls.
const UpdatedAgo = ({ at }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 10000);
    return () => clearInterval(t);
  }, []);
  if (!at) return null;
  const secs = Math.max(0, Math.round((Date.now() - at) / 1000));
  const label = secs < 60 ? `${secs}s ago` : `${Math.round(secs / 60)}m ago`;
  return (
    // Fixed min-width: the seconds label changes length as it ticks; without
    // reserved space it nudges the month switcher sideways.
    <span className="inline-flex min-w-[10rem] items-center justify-end gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      Live · updated {label}
    </span>
  );
};

// The CM filter treats "no CM" as a selectable value too — filtering to the
// unassigned rows is exactly how you find the assignment gaps.
const cmKeyOf = (name) => name || 'none';

// Clickable person name: click = show only their rows, click again = clear.
const CmFilterName = ({
  name,
  activeKey,
  onToggle,
  className,
  emptyLabel = 'No CM assigned',
}) => {
  const key = cmKeyOf(name);
  const active = activeKey === key;
  return (
    <button
      type="button"
      onClick={() => onToggle(key)}
      title={
        active
          ? 'Clear the filter'
          : `Show only ${name || emptyLabel.toLowerCase()} rows`
      }
      className={cn(
        'inline-flex min-w-0 items-center gap-1 text-xs transition-colors',
        active
          ? 'font-semibold text-blue-700'
          : 'text-muted-foreground hover:text-foreground hover:underline',
        className,
      )}
    >
      <UserCheck className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{name || emptyLabel}</span>
    </button>
  );
};

// Copy the visible rows as plain numbered lists ("Ryan Farrell Manual
// Reviews: 1. Devon Butler ..."), for pasting into Teams/email. Filtered =
// one section; unfiltered = one section PER person, stacked — as if each
// name had been filtered and copied by hand. `sections` = [{title, names}].
const CopyListButton = ({ sections }) => {
  const [copied, setCopied] = useState(false);
  const total = sections.reduce((sum, s) => sum + s.names.length, 0);
  const copy = async () => {
    const text = sections
      .filter((s) => s.names.length > 0)
      .map((s) =>
        [
          s.title,
          ...s.names.map((name, i) => `${i + 1}. ${name || 'Unknown client'}`),
        ].join('\n'),
      )
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Clipboard access denied (non-secure context) — nothing to break.
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      disabled={total === 0}
      title="Copy the rows currently showing as numbered lists, grouped by person"
      className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3 text-emerald-600" /> Copied
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" /> Copy list
        </>
      )}
    </button>
  );
};

// Group visible rows into copy sections by person: alphabetical, unassigned
// last. titleFor(nameOrNull) builds each section heading.
const buildCopySections = (rows, nameOf, titleFor) => {
  const byPerson = new Map();
  rows.forEach((row) => {
    const name = nameOf(row) || null;
    if (!byPerson.has(name)) byPerson.set(name, []);
    byPerson.get(name).push(row.clientName);
  });
  return [...byPerson.entries()]
    .sort(([a], [b]) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    })
    .map(([name, names]) => ({ title: titleFor(name), names }));
};

// Small dismissible pill for the section header while a CM filter is on.
const CmFilterPill = ({ name, onClear, emptyLabel = 'no CM assigned' }) => (
  <button
    type="button"
    onClick={onClear}
    title="Clear the filter"
    className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-200"
  >
    Only {name || emptyLabel}
    <span aria-hidden>×</span>
  </button>
);

// Fixed grid so every column lines up vertically down the section:
// name | reason (+meta) | person | email | status. `person`/`emptyLabel`/
// `emailTo` flex per card: CM reviews show the case manager, onboarding
// reviews show the client's Owner (or the onboarding-admin fanout).
const ReviewRow = ({
  item,
  cmFilter,
  onCmToggle,
  person,
  emptyLabel = 'No CM assigned',
  emailTo = 'CM',
}) => (
  // Every column except the flexible reason cell is FIXED so rows can never
  // drift out of vertical alignment (each row is its own grid; content-sized
  // columns like `auto` resolve per row — a wide "Handled Aug 4" chip was
  // shoving that row's columns left).
  <div className="grid grid-cols-[12rem_minmax(0,1fr)_10rem_7.5rem_9.5rem] items-center gap-x-4 px-5 py-2.5">
    <ClaimLink
      claimId={item.claimId}
      className="truncate text-sm font-medium text-foreground hover:underline"
    >
      {item.clientName || 'Unknown client'}
    </ClaimLink>
    <span className="min-w-0 truncate text-xs text-muted-foreground">
      <span
        className="cursor-help underline decoration-dotted decoration-border underline-offset-2"
        title={REASON_HELP[item.reason] || undefined}
      >
        {REASON_LABELS[item.reason] || item.reason}
      </span>
      {item.isRepeat ? (
        <span title="This item has re-appeared on every run since it was first flagged.">
          {' '}
          · since {fmtDate(item.since)}
        </span>
      ) : null}
      {item.occurrences > 1 ? (
        <span title="This month had more than one run; the same outcome was flagged on each. Shown once.">
          {' '}
          · flagged {item.occurrences}x
        </span>
      ) : null}
    </span>
    <CmFilterName
      name={person !== undefined ? person : item.cm?.name}
      activeKey={cmFilter}
      onToggle={onCmToggle}
      emptyLabel={emptyLabel}
    />
    {/* Always render the email CELL: EmailIndicator returns null when no
        email matched, and a missing grid child shifts every later column
        left — rows with and without chips must stay aligned. */}
    <span className="min-w-0">
      <EmailIndicator email={item.email} to={emailTo} />
    </span>
    <span className="justify-self-end">
      <HandledChip item={item} />
    </span>
  </div>
);

const VaRunBoardPage = () => {
  const [month, setMonth] = useState(() => dayjs().format('YYYY-MM'));
  const { data, isLoading, isFetching, dataUpdatedAt } = useVaRunReport(month);

  const [showParked, setShowParked] = useState(false);
  const [showNoChange, setShowNoChange] = useState(false);

  // Per-section CM filters (click a CM name to toggle). Cleared on month
  // change so a stale filter can't make a fresh month look empty.
  const [reviewCmFilter, setReviewCmFilter] = useState(null);
  const [onboardingCmFilter, setOnboardingCmFilter] = useState(null);
  const [parkedCmFilter, setParkedCmFilter] = useState(null);
  useEffect(() => {
    setReviewCmFilter(null);
    setOnboardingCmFilter(null);
    setParkedCmFilter(null);
  }, [month]);
  const toggleReviewCm = (key) =>
    setReviewCmFilter((prev) => (prev === key ? null : key));
  const toggleOnboardingCm = (key) =>
    setOnboardingCmFilter((prev) => (prev === key ? null : key));
  const toggleParkedCm = (key) =>
    setParkedCmFilter((prev) => (prev === key ? null : key));

  const invoicesRef = useRef(null);
  const reviewsRef = useRef(null);
  const failedRef = useRef(null);
  const noChangeRef = useRef(null);
  const parkedRef = useRef(null);

  const scrollToSection = (ref) =>
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const monthLabel = dayjs(`${month}-01`).format('MMMM YYYY');
  const atCurrentMonth = month === dayjs().format('YYYY-MM');
  const shiftMonth = (delta) =>
    setMonth(dayjs(`${month}-01`).add(delta, 'month').format('YYYY-MM'));

  const s = data?.summary;
  const successRate =
    s && s.claimsCalled > 0
      ? Math.round(
          (100 * s.results.ok) /
            Math.max(1, Object.values(s.results).reduce((a, b) => a + b, 0)),
        )
      : null;

  const newReviews = useMemo(
    () =>
      (data?.reviews || []).filter(
        (r) =>
          !r.isRepeat &&
          (!reviewCmFilter || cmKeyOf(r.cm?.name) === reviewCmFilter),
      ),
    [data, reviewCmFilter],
  );
  const repeatReviews = useMemo(
    () =>
      (data?.reviews || []).filter(
        (r) =>
          r.isRepeat &&
          (!reviewCmFilter || cmKeyOf(r.cm?.name) === reviewCmFilter),
      ),
    [data, reviewCmFilter],
  );
  // Display name for the active review filter pill (from any matching row).
  const reviewCmFilterName = useMemo(() => {
    if (!reviewCmFilter) return null;
    if (reviewCmFilter === 'none') return null;
    const hit = (data?.reviews || []).find(
      (r) => cmKeyOf(r.cm?.name) === reviewCmFilter,
    );
    return hit?.cm?.name || null;
  }, [data, reviewCmFilter]);

  const newOnboardingReviews = useMemo(
    () =>
      (data?.onboardingReviews || []).filter(
        (r) =>
          !r.isRepeat &&
          (!onboardingCmFilter ||
            cmKeyOf(r.owner?.name) === onboardingCmFilter),
      ),
    [data, onboardingCmFilter],
  );
  const repeatOnboardingReviews = useMemo(
    () =>
      (data?.onboardingReviews || []).filter(
        (r) =>
          r.isRepeat &&
          (!onboardingCmFilter ||
            cmKeyOf(r.owner?.name) === onboardingCmFilter),
      ),
    [data, onboardingCmFilter],
  );
  const onboardingCmFilterName = useMemo(() => {
    if (!onboardingCmFilter || onboardingCmFilter === 'none') return null;
    const hit = (data?.onboardingReviews || []).find(
      (r) => cmKeyOf(r.owner?.name) === onboardingCmFilter,
    );
    return hit?.owner?.name || null;
  }, [data, onboardingCmFilter]);

  const parkedFiltered = useMemo(
    () =>
      (data?.parked || []).filter(
        (p) => !parkedCmFilter || cmKeyOf(p.cmName) === parkedCmFilter,
      ),
    [data, parkedCmFilter],
  );
  const parkedCmFilterName = useMemo(() => {
    if (!parkedCmFilter || parkedCmFilter === 'none') return null;
    const hit = (data?.parked || []).find(
      (p) => cmKeyOf(p.cmName) === parkedCmFilter,
    );
    return hit?.cmName || null;
  }, [data, parkedCmFilter]);

  return (
    <PrivateLayout>
      <div className="mx-auto max-w-5xl space-y-5 pb-10">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-foreground">
              <Activity className="h-5 w-5 text-violet-600" />
              VA Run Board
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Live status of the monthly automated rating checks. Updates on its
              own as CMs confirm ratings and invoices move.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <UpdatedAgo at={dataUpdatedAt} />
            {/* Fixed-width slot: the spinner appears WITHIN reserved space so
                the month switcher never shifts while a refetch is in flight. */}
            <span className="flex w-3.5 shrink-0 items-center justify-center">
              <RefreshCw
                className={cn(
                  'h-3.5 w-3.5 animate-spin text-muted-foreground',
                  isFetching && !isLoading ? 'visible' : 'invisible',
                )}
              />
            </span>
            <div className="flex items-center rounded-lg border border-border bg-card">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                className="px-2 py-1.5 text-muted-foreground hover:text-foreground"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[8.5rem] text-center text-sm font-semibold text-foreground">
                {monthLabel}
              </span>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                disabled={atCurrentMonth}
                className={cn(
                  'px-2 py-1.5',
                  atCurrentMonth
                    ? 'cursor-default text-border'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading run data…
          </div>
        ) : !s ? (
          <div className="rounded-xl border border-border bg-card py-16 text-center text-muted-foreground">
            Could not load the run report.
          </div>
        ) : !s.ranThisMonth ? (
          <div className="rounded-xl border border-border bg-card px-6 py-14 text-center">
            <PhoneCall className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium text-foreground">
              No calls recorded in {monthLabel}.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {atCurrentMonth
                ? `The batch runs on the 1st. Next run: ${fmtDate(s.schedule.nextRunDate)}.`
                : 'The batch did not run this month.'}
            </p>
          </div>
        ) : (
          <>
            {/* Hero tiles */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <HeroTile
                icon={PhoneCall}
                accent="blue"
                label="Called"
                value={s.claimsCalled}
                sub={`of ${s.eligible} eligible${s.parkedCount > 0 ? ` · ${s.parkedCount} parked` : ''}`}
              />
              <HeroTile
                icon={Check}
                accent="emerald"
                label="Success"
                value={successRate != null ? `${successRate}%` : '—'}
                sub={`${s.results.ok} ok / ${s.results.no_info + s.results.sys_unavail + s.results.error} failed`}
              />
              <HeroTile
                icon={CircleDollarSign}
                accent="violet"
                label="Increases found"
                value={money(s.invoices.total)}
                sub={`${s.invoices.confirmed} of ${s.invoices.count} confirmed`}
                onClick={() => scrollToSection(invoicesRef)}
              />
              <HeroTile
                icon={AlertTriangle}
                accent="amber"
                label="Reviews open"
                value={s.reviews.open}
                sub={`${s.reviews.handled} handled`}
                onClick={() => scrollToSection(reviewsRef)}
              />
              <HeroTile
                icon={PhoneMissed}
                accent="red"
                label="Still failing"
                value={s.failedCalls.stillFailing}
                sub={`${s.failedCalls.recovered} recovered`}
                onClick={() => scrollToSection(failedRef)}
              />
            </div>

            {/* System issues: only ever rendered when something actually broke */}
            {data.systemIssues.length > 0 && (
              <SectionCard
                icon={AlertTriangle}
                accent="red"
                title="System issues"
                badges={<CountPill tone="bg-red-100 text-red-600">{data.systemIssues.length}</CountPill>}
                explainer="Our own machinery failed on these, for example invoice creation crashed after a successful call. This is not client work and no client was billed. It needs an engineer to look at the claim."
              >
                <div className="divide-y divide-border">
                  {data.systemIssues.map((i) => (
                    <div key={`${i.claimId}-${i.reason}`} className="grid grid-cols-[minmax(10rem,12rem)_minmax(0,1fr)_auto] items-center gap-x-4 px-5 py-2.5">
                      <ClaimLink claimId={i.claimId} className="truncate text-sm font-medium hover:underline">
                        {i.clientName}
                      </ClaimLink>
                      <span className="truncate text-xs text-status-error-fg">{i.reason}</span>
                      <span className="justify-self-end text-xs text-muted-foreground">{fmtDate(i.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Invoices */}
            <SectionCard
              innerRef={invoicesRef}
              icon={CircleDollarSign}
              accent="violet"
              title="Rating increases found"
              badges={
                <>
                  {s.invoices.count > 0 && s.invoices.confirmed < s.invoices.count ? (
                    <CountPill tone="bg-indigo-100 text-indigo-700">
                      {s.invoices.count - s.invoices.confirmed} draft
                      {s.invoices.count - s.invoices.confirmed === 1 ? '' : 's'} to confirm
                    </CountPill>
                  ) : null}
                  {s.invoices.count > 0 && s.invoices.confirmed === s.invoices.count ? (
                    <CountPill tone="bg-emerald-100 text-emerald-700">All confirmed</CountPill>
                  ) : null}
                  {s.invoices.paid > 0 ? (
                    <CountPill tone="bg-emerald-100 text-emerald-700">{s.invoices.paid} paid</CountPill>
                  ) : null}
                </>
              }
              explainer="The call found a higher rating than we had on file, so a draft Stripe invoice was created for the fee difference. Nothing has been sent to the client yet. Review and confirm each draft from the Invoices page; confirming sends the invoice email. The status pill here moves from Draft to Open to Paid on its own."
            >
              {data.invoices.length === 0 ? (
                <EmptyRow>No rating increases were found this month.</EmptyRow>
              ) : (
                <div className="divide-y divide-border">
                  {data.invoices.map((inv) => (
                    <div key={inv.claimBillingEventId} className="grid grid-cols-[minmax(10rem,12rem)_7.5rem_1fr_auto_4.5rem] items-center gap-x-4 px-5 py-2.5">
                      <ClaimLink
                        claimId={inv.claimId}
                        className="truncate text-sm font-medium text-foreground hover:underline"
                      >
                        {inv.clientName}
                      </ClaimLink>
                      <RatingChange from={inv.previousRating} to={inv.newRating} />
                      <span />
                      <span className="justify-self-end text-sm font-semibold text-foreground">
                        {money(inv.invoiceBilledAmount)}
                      </span>
                      <span className="justify-self-end">
                        {inv.invoiceStatus === 'deleted' ? (
                          <span
                            className="inline-flex items-center whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                            title="The draft invoice for this increase was deleted by staff. The increase stays on the board for the record; re-invoice from the claim's Billing tab if that was a mistake."
                          >
                            Deleted
                          </span>
                        ) : (
                          <InvoiceStatusPill status={inv.invoiceStatus} />
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            {/* Legacy attention */}
            <SectionCard
              icon={ScrollText}
              accent="amber"
              title="Needs a manual invoice (legacy billing)"
              badges={
                <>
                  {data.legacyAttention.filter((l) => !l.handled).length > 0 ? (
                    <CountPill tone="bg-amber-100 text-amber-700">
                      {data.legacyAttention.filter((l) => !l.handled).length} open
                    </CountPill>
                  ) : null}
                  {data.legacyAttention.filter((l) => l.handled).length > 0 ? (
                    <CountPill tone="bg-emerald-100 text-emerald-700">
                      {data.legacyAttention.filter((l) => l.handled).length} handled
                    </CountPill>
                  ) : null}
                </>
              }
              explainer="These clients are on pre-2025 legacy agreements, so the system never invoices them automatically. When one shows an increase, an email goes to the billing review inbox and the invoice must be created by hand from the claim's Billing tab. Rows flip to Handled here once that invoice exists. No-change rows need nothing."
            >
              {data.legacyAttention.length === 0 ? (
                <EmptyRow>No legacy-billing items this month.</EmptyRow>
              ) : (
                <div className="divide-y divide-border">
                  {data.legacyAttention.map((item) => (
                    <div key={item.claimBillingEventId} className="grid grid-cols-[minmax(10rem,12rem)_7.5rem_minmax(0,1fr)_9.5rem_auto] items-center gap-x-4 px-5 py-2.5">
                      <ClaimLink
                        claimId={item.claimId}
                        className="truncate text-sm font-medium text-foreground hover:underline"
                      >
                        {item.clientName}
                      </ClaimLink>
                      <RatingChange from={item.previousRating} to={item.newRating} />
                      <span className="min-w-0 truncate text-xs">
                        {item.newRating > (item.previousRating || 0) ? (
                          <span className="rounded-full bg-status-warning-bg px-2 py-0.5 font-medium text-status-warning-fg">
                            Increase
                          </span>
                        ) : (
                          <span className="text-muted-foreground">no change</span>
                        )}
                        {item.occurrences > 1 ? (
                          <span
                            className="text-muted-foreground"
                            title="This month had more than one run; the same outcome was flagged on each. Shown once."
                          >
                            {' '}
                            · flagged {item.occurrences}x
                          </span>
                        ) : null}
                      </span>
                      <EmailIndicator email={item.email} to="billing inbox" />
                      <span className="justify-self-end">
                        <HandledChip item={item} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            {/* Manual reviews */}
            <SectionCard
              innerRef={reviewsRef}
              icon={UserCheck}
              accent="blue"
              title="Manual reviews for CMs"
              badges={
                <>
                  <CopyListButton
                    sections={buildCopySections(
                      [...newReviews, ...repeatReviews],
                      (r) => r.cm?.name,
                      (name) =>
                        name
                          ? `${name} Manual Reviews:`
                          : 'Manual Reviews (no CM assigned):',
                    )}
                  />
                  {reviewCmFilter ? (
                    <CmFilterPill
                      name={reviewCmFilterName}
                      onClear={() => setReviewCmFilter(null)}
                    />
                  ) : null}
                  {(s.cmReviews || s.reviews).open > 0 ? (
                    <CountPill tone="bg-amber-100 text-amber-700">{(s.cmReviews || s.reviews).open} open</CountPill>
                  ) : null}
                  {(s.cmReviews || s.reviews).handled > 0 ? (
                    <CountPill tone="bg-emerald-100 text-emerald-700">{(s.cmReviews || s.reviews).handled} handled</CountPill>
                  ) : null}
                  {(s.cmReviews || s.reviews).open === 0 &&
                  (s.cmReviews || s.reviews).total > 0 ? (
                    <CountPill tone="bg-emerald-100 text-emerald-700">All handled</CountPill>
                  ) : null}
                </>
              }
              explainer="The call reached the VA and got a payment amount, but the system could not confidently translate it into a rating, so nothing was billed. The assigned CM was emailed and should confirm the client's rating with Update Rating on the claim profile. Rows flip to Handled automatically once that happens; unresolved items re-appear on every run. Click a CM name to see only their rows."
            >
              {newReviews.length === 0 && repeatReviews.length === 0 ? (
                <EmptyRow>
                  {reviewCmFilter
                    ? 'No reviews match the CM filter this month.'
                    : 'No manual reviews this month.'}
                </EmptyRow>
              ) : (
                <>
                  {newReviews.length > 0 && (
                    <>
                      <div className="bg-muted/40 px-5 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        New this month ({newReviews.length})
                      </div>
                      <div className="divide-y divide-border">
                        {newReviews.map((item) => (
                          <ReviewRow
                            key={item.claimBillingEventId}
                            item={item}
                            cmFilter={reviewCmFilter}
                            onCmToggle={toggleReviewCm}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {repeatReviews.length > 0 && (
                    <>
                      <div className="bg-muted/40 px-5 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Repeating from earlier runs ({repeatReviews.length})
                      </div>
                      <div className="divide-y divide-border">
                        {repeatReviews.map((item) => (
                          <ReviewRow
                            key={item.claimBillingEventId}
                            item={item}
                            cmFilter={reviewCmFilter}
                            onCmToggle={toggleReviewCm}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </SectionCard>

            {/* Manual reviews for Onboarding: signup baseline calls whose
                amount could not be translated into a STARTING rating. Their
                emails went to the client's Owner / the onboarding admins,
                never a CM — mixing them into the CM card mislabeled them. */}
            {(data.onboardingReviews || []).length > 0 ? (
              <SectionCard
                icon={UserCheck}
                accent="violet"
                title="Manual reviews for Onboarding"
                badges={
                  <>
                    <CopyListButton
                      sections={buildCopySections(
                        [...newOnboardingReviews, ...repeatOnboardingReviews],
                        (r) => r.owner?.name,
                        (name) =>
                          name
                            ? `${name} Onboarding Reviews:`
                            : 'Onboarding Reviews (onboarding admins):',
                      )}
                    />
                    {onboardingCmFilter ? (
                      <CmFilterPill
                        name={onboardingCmFilterName}
                        onClear={() => setOnboardingCmFilter(null)}
                        emptyLabel="onboarding admins"
                      />
                    ) : null}
                    {s.onboardingReviews?.open > 0 ? (
                      <CountPill tone="bg-amber-100 text-amber-700">
                        {s.onboardingReviews.open} open
                      </CountPill>
                    ) : null}
                    {s.onboardingReviews?.handled > 0 ? (
                      <CountPill tone="bg-emerald-100 text-emerald-700">
                        {s.onboardingReviews.handled} handled
                      </CountPill>
                    ) : null}
                  </>
                }
                explainer="These came from the baseline call made when the client first signed up: the call reached the VA, but the amount could not be translated into a STARTING rating, so the client has no baseline yet. The client's Owner was emailed (or the onboarding admins when nobody had claimed the client). Confirm the rating with Update Rating on the claim profile. Click a name to see only their rows."
              >
                {newOnboardingReviews.length === 0 &&
                repeatOnboardingReviews.length === 0 ? (
                  <EmptyRow>No onboarding reviews match the filter.</EmptyRow>
                ) : (
                  <>
                    {newOnboardingReviews.length > 0 && (
                      <>
                        <div className="bg-muted/40 px-5 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          New this month ({newOnboardingReviews.length})
                        </div>
                        <div className="divide-y divide-border">
                          {newOnboardingReviews.map((item) => (
                            <ReviewRow
                              key={item.claimBillingEventId}
                              item={item}
                              cmFilter={onboardingCmFilter}
                              onCmToggle={toggleOnboardingCm}
                              person={item.owner?.name || null}
                              emptyLabel="Onboarding admins"
                              emailTo="Owner"
                            />
                          ))}
                        </div>
                      </>
                    )}
                    {repeatOnboardingReviews.length > 0 && (
                      <>
                        <div className="bg-muted/40 px-5 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Repeating from earlier runs (
                          {repeatOnboardingReviews.length})
                        </div>
                        <div className="divide-y divide-border">
                          {repeatOnboardingReviews.map((item) => (
                            <ReviewRow
                              key={item.claimBillingEventId}
                              item={item}
                              cmFilter={onboardingCmFilter}
                              onCmToggle={toggleOnboardingCm}
                              person={item.owner?.name || null}
                              emptyLabel="Onboarding admins"
                              emailTo="Owner"
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </SectionCard>
            ) : null}

            {/* Failed calls */}
            <SectionCard
              innerRef={failedRef}
              icon={PhoneMissed}
              accent="red"
              title="Failed calls"
              badges={
                <>
                  {s.failedCalls.stillFailing > 0 ? (
                    <CountPill tone="bg-red-100 text-red-600">
                      {s.failedCalls.stillFailing} still failing
                    </CountPill>
                  ) : null}
                  {s.failedCalls.recovered > 0 ? (
                    <CountPill tone="bg-emerald-100 text-emerald-700">
                      {s.failedCalls.recovered} recovered
                    </CountPill>
                  ) : null}
                </>
              }
              explainer={`The VA line connected but gave no information for these clients. Two different flows dial: Increase checks retry automatically with a second wave the same day, then each monthly run (next: ${fmtDate(s.schedule.nextRunDate)}) — one-off failures usually recover on their own. Onboarding baseline calls fire when a client signs up and run their own, faster retry ladder to establish the starting rating. In both flows, a claim that keeps failing usually has an SSN or date of birth that does not match VA records; after repeated straight misses the responsible person (the CM, or the client's Owner for onboarding calls) gets one email and the claim moves to the parked list at the bottom of this page.`}
            >
              {data.failedCalls.length === 0 ? (
                <EmptyRow>Every call collected a payment amount. Clean run.</EmptyRow>
              ) : (
                [
                  {
                    key: 'batch',
                    label: 'Increase checks (Submitted)',
                    rows: data.failedCalls.filter((f) => f.flow !== 'onboarding'),
                  },
                  {
                    key: 'onboarding',
                    label: 'Onboarding baseline calls',
                    rows: data.failedCalls.filter((f) => f.flow === 'onboarding'),
                  },
                ]
                  .filter((group) => group.rows.length > 0)
                  .map((group) => (
                    <React.Fragment key={group.key}>
                      <div className="bg-muted/40 px-5 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {group.label} ({group.rows.length})
                      </div>
                      <div className="divide-y divide-border">
                        {group.rows.map((f) => (
                    <div key={f.claimId} className="grid grid-cols-[minmax(10rem,12rem)_4rem_minmax(0,1fr)_auto] items-center gap-x-4 px-5 py-2.5">
                      <ClaimLink
                        claimId={f.claimId}
                        className="truncate text-sm font-medium text-foreground hover:underline"
                      >
                        {f.clientName || 'Unknown client'}
                      </ClaimLink>
                      {/* attempt timeline dots */}
                      <span className="inline-flex items-center gap-1" title={f.timeline.map((t) => `${dayjs(t.at).format('MMM D h:mm A')}: ${t.result}`).join('\n')}>
                        {f.timeline.map((t, i) => (
                          <span
                            key={i}
                            className={cn(
                              'h-2 w-2 rounded-full',
                              t.result === 'ok' ? 'bg-emerald-500' : 'bg-red-400',
                            )}
                          />
                        ))}
                      </span>
                      <span className="min-w-0 truncate text-xs text-muted-foreground">
                        {f.attemptsThisMonth} attempt{f.attemptsThisMonth === 1 ? '' : 's'}
                        {f.failureStreak ? ` · streak ${f.failureStreak}` : ''}
                        {!f.recovered
                          ? ` · ${f.latestResult === 'sys_unavail' ? 'VA system unavailable' : f.latestResult === 'error' ? 'call error' : 'VA line gave no info'}`
                          : ''}
                      </span>
                      <span className="justify-self-end">
                        {f.recovered ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-status-success-bg px-2 py-0.5 text-xs font-medium text-status-success-fg">
                            <Check className="h-3 w-3" /> Recovered
                          </span>
                        ) : (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                            Awaiting retry
                          </span>
                        )}
                      </span>
                    </div>
                        ))}
                      </div>
                    </React.Fragment>
                  ))
              )}
            </SectionCard>

            {/* Quiet outcomes: same section shell as everything above, with
                only the long name lists collapsed behind Show all. */}
            <SectionCard
              innerRef={noChangeRef}
              icon={Check}
              accent="emerald"
              title="No rating change"
              badges={
                <>
                  <CountPill tone="bg-emerald-100 text-emerald-700">
                    {s.noChangeCount} clients
                  </CountPill>
                  {s.matchedManualCount > 0 ? (
                    <CountPill tone="bg-emerald-100 text-emerald-700">
                      {s.matchedManualCount} matched manual
                    </CountPill>
                  ) : null}
                </>
              }
              explainer="Nothing to do here. These calls succeeded and the payment matched the rating already on file, so there was nothing to bill. Ratings that matched a manual entry were confirmed and closed automatically."
            >
              <ShowAllRow
                open={showNoChange}
                onToggle={() => setShowNoChange((v) => !v)}
                label={`Show all ${s.noChangeCount} clients`}
              />
              {showNoChange ? (
                <div className="border-t border-border px-5 py-3 text-xs leading-6 text-muted-foreground">
                  {data.noChange.map((c) => `${c.clientName} (${c.rating}%)`).join(' · ') || 'None'}
                </div>
              ) : null}
            </SectionCard>

            <SectionCard
              innerRef={parkedRef}
              icon={PauseCircle}
              accent="slate"
              title="Parked, no longer being called"
              badges={
                <>
                  {parkedCmFilter ? (
                    <CmFilterPill
                      name={parkedCmFilterName}
                      onClear={() => setParkedCmFilter(null)}
                    />
                  ) : null}
                  <CountPill tone="bg-slate-100 text-slate-600">
                    {s.parkedCount} claims
                  </CountPill>
                </>
              }
              explainer={
                <>
                  The batch stopped dialing these claims after too many failed
                  calls in a row.{' '}
                  <strong>
                    This is only the log of the last automated call attempts —
                    many of these clients have very likely been taken care of
                    since through normal work on their claims.
                  </strong>{' '}
                  Click a CM name to see only their claims.
                </>
              }
            >
              <ShowAllRow
                open={showParked}
                onToggle={() => setShowParked((v) => !v)}
                label={`Show all ${s.parkedCount} claims`}
              />
              {showParked ? (
                <div className="divide-y divide-border border-t border-border">
                  {parkedFiltered.length === 0 ? (
                    <EmptyRow>No parked claims match the CM filter.</EmptyRow>
                  ) : (
                    parkedFiltered.map((p) => (
                      <div key={p.claimId} className="grid grid-cols-[12rem_minmax(0,1fr)_10rem] items-center gap-x-4 px-5 py-2">
                        <ClaimLink claimId={p.claimId} className="truncate text-sm hover:underline">
                          {p.clientName}
                        </ClaimLink>
                        <span className="min-w-0 truncate text-xs text-muted-foreground">
                          last attempt {fmtDate(p.lastAttemptAt)}
                          {p.lastStreak ? ` · streak ${p.lastStreak}` : ''}
                        </span>
                        <CmFilterName
                          name={p.cmName}
                          activeKey={parkedCmFilter}
                          onToggle={toggleParkedCm}
                          className="justify-self-end"
                        />
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </SectionCard>
          </>
        )}
      </div>
    </PrivateLayout>
  );
};

export default VaRunBoardPage;
